import React, { Fragment, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { useLocation, useParams } from 'react-router-dom';
import ReactInfiniteScroller from 'react-infinite-scroller';

import styles from './styles.scss';
import {
  AppBar,
  AppBarSection,
  AppBarTitle,
  Checkbox,
  getNextSortDirection,
} from '@densityco/ui';
import ListView, { ListViewColumn, ListViewColumnSpacer } from '../ListView';
import HorizontalForm from '../HorizontalForm';

import colorVariables from '@densityco/ui/variables/colors.json';

import useRxStore from '../../utils/use-rx-store';
import useRxDispatch from '../../utils/use-rx-dispatch';
import singleClickDetector from '../../utils/single-click-detector';
import { openInNewTab } from '../../utils/navigation';
import { deserializeListViewState } from '../../utils/rx-store-serialization';

import ActionBar, { FilterOperatorInputMultiChoices, FilterOperatorInputText, FilterOperatorInputPercent } from '../ActionBar';
import { SortDirection } from '../../types/sort';
import { deserializeOrganization } from '../../types/all-organizations';
import { Choice } from '../../types/choice';
import { FilterOperator } from '../../types/filters';
import SpaceNameAncestry from '../SpaceNameAncestry';
import SensorActionButtons from '../Sensors/ActionButtons';
import Loading from '../Loading';
import ErrorState from '../ErrorState';
import ListViewTwoRowHeader from '../ListViewTwoRowHeader';
import DetailsButton from '../DetailsButton';

import ImplementedStore from '../../rx-stores/implemented';
import {
  ImplementedState,
  ImplementedActionTypes,
} from '../../types/implemented';
import { Space } from '../../types/api';
import { CaptureScheduleModalActionTypes } from '../../types/capture-schedule-modal';
import { ResourceStatus } from '../../types/resource';
import { SPACE_FUNCTION_CHOICES } from '@densityco/lib-space-helpers'

export const Implemented: React.FunctionComponent<{}> = props => {
  const state = useRxStore(ImplementedStore);
  const dispatch = useRxDispatch();
  const location = useLocation();
  const { organizationIdOrAll } = useParams();

  let scrollParentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { filters, sort, searchText } = deserializeListViewState(
      location.search.replace(/^\?/, ''),
    );

    dispatch({
      type: ImplementedActionTypes.IMPLEMENTED_CHANGE_PAGE,
      searchText,
      filters,
      sort,
      organizationId: deserializeOrganization(organizationIdOrAll),
    });
  }, [organizationIdOrAll]);

  const selectedSensors = (
    state.resource.status === ResourceStatus.COMPLETE
      ? state.resource.data.spaces
        .filter(space =>
          state.selectedSpaceIds.includes(space.external_id),
        )
        .flatMap(space =>
          space.installations.map(sensor => ({
            space,
            sensor,
          })),
        )
        .reduce((acc, { space, sensor }) => {
          // Deduplicate the merged sensor list
          if (!acc.find(i => i.sensor.serial_number === sensor.serial_number)) {
            return [...acc, { space, sensor }];
          } else {
            return acc;
          }
        }, [] as Array<{ space: Space; sensor: Space['installations'][0] }>)
        .map(({ space, sensor }) => ({
          serial_number: sensor.serial_number,
          link_id: sensor.link_id,
          organization: {
            id: space.organization_external_id,
            name: space.organization_name,
          },
          sensor_type: sensor.sensor_type,
          os_version: sensor.os_version,
          doorway_name: sensor.doorway_name,
          diagnostic_info_json: sensor.diagnostic_info_json,
          in_mpi: sensor.in_mpi,
        }))
      : []
  );

  const header = (
    <Fragment>
      <AppBar>
        <AppBarTitle>Implemented Spaces</AppBarTitle>
      </AppBar>
      <ActionBar
        units="Spaces"
        totalItemCount={null}
        selectedItemCount={state.selectedSpaceIds.length}
        onClearSelectedItems={() =>
          dispatch({
            type: ImplementedActionTypes.IMPLEMENTED_CHANGE_CHECKS,
            ids: state.selectedSpaceIds,
            checked: false,
          })
        }
        selectedActionButtons={
          <SensorActionButtons
            presentation="list"
            sensors={selectedSensors}
          />
        }
        showSearch
        searchPlaceholder="Search by space name or id"
        searchWidth={375}
        searchValue={state.searchText}
        onChangeSearch={e =>
          dispatch({
            type: ImplementedActionTypes.IMPLEMENTED_SET_SEARCH_TEXT,
            text: e.target.value,
          })
        }
        filterDefinitions={[
          {
            id: 'space_function',
            label: 'Space Function',
            operators: [
              {
                id: 'in',
                label: 'is one of',
                allowMultiple: true,
                isValueValid: state => state.every(i => SPACE_FUNCTION_CHOICES.map(s => s.id).includes(i)),
                initialValue: [],
                component: (props) => <FilterOperatorInputMultiChoices choices={SPACE_FUNCTION_CHOICES} {...props} />,
              } as FilterOperator<Array<Choice['id']>>,
            ],
          },
          {
            id: 'capacity',
            label: 'Capacity',
            operators: [
              {
                id: 'gt',
                label: 'is greater than',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'gte',
                label: 'is greater than or equal to',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'lt',
                label: 'is less than',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'lte',
                label: 'is less than or equal to',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
            ],
          },
          {
            id: 'peak_count',
            label: 'Peak Count',
            operators: [
              {
                id: 'gt',
                label: 'is greater than',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'gte',
                label: 'is greater than or equal to',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'lt',
                label: 'is less than',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
              {
                id: 'lte',
                label: 'is less than or equal to',
                allowMultiple: false,
                isValueValid: s => !isNaN(Number(s)),
                initialValue: '',
                component: FilterOperatorInputText,
              } as FilterOperator<string>,
            ],
          },
        ]}
        filters={state.filters}
        onChangeFilters={filters =>
          dispatch({
            type: ImplementedActionTypes.IMPLEMENTED_SET_FILTERS,
            filters,
          })
        }
      />
    </Fragment>
  );

  switch (state.resource.status) {
    case ResourceStatus.IDLE:
      return <p>Idle</p>;
    case ResourceStatus.LOADING:
      return (
        <div className={styles.spacesContainer}>
          {header}
          <Loading />
        </div>
      );
    case ResourceStatus.ERROR:
      return (
        <div className={styles.spacesContainer}>
          {header}
          <ErrorState error={state.resource.error} />
        </div>
      );
    case ResourceStatus.COMPLETE:
      const resourceContent = state.resource;
      const visibleData = resourceContent.data.spaces.filter(space => space.installations.length);

      // Determine if all items are checked, because if so, highlight the checkbox in the listview header
      const selectedSpaceIdSet = new Set(state.selectedSpaceIds);
      const allVisibleDataChecked =
        visibleData.length > 0 &&
        visibleData.every(item => selectedSpaceIdSet.has(item.external_id));

      return (
        <Fragment>
          {header}

          <div className={styles.spacesList} ref={scrollParentRef}>
            <ReactInfiniteScroller
              pageStart={1}
              loadMore={page => {
                dispatch({
                  type: ImplementedActionTypes.IMPLEMENTED_SET_PAGE,
                  value: page,
                });
              }}
              hasMore={
                state.resource.data.total > state.resource.data.spaces.length &&
                state.resource.data.fetchingMoreSpaces.status ===
                ResourceStatus.IDLE
              }
              useWindow={false}
              getScrollParent={() => scrollParentRef.current}
            >
              <div data-test-id="implemented-list-view">
                <ListView
                  showHeaders={true}
                  padOuterColumns={true}
                  data={visibleData}
                  sort={[state.sort]}
                  headerHeight={48}
                  onClickHeader={(column: string) => {
                    // Don't allow fitlering these columns
                    if (
                      column === 'Checkboxes' ||
                      column === 'Details'
                    ) {
                      return;
                    }

                    const lastSortDirection =
                      column === state.sort.column
                        ? state.sort.direction
                        : 'none';
                    const sortDirection: SortDirection = getNextSortDirection(lastSortDirection);

                    dispatch({
                      type: ImplementedActionTypes.IMPLEMENTED_SET_SORT,
                      column: column,
                      direction: sortDirection,
                    });
                  }}
                  onMouseDownRow={item => {
                    return singleClickDetector(() => {
                      openInNewTab(
                        `#/${item.organization_external_id}/spaces/${item.external_id}`,
                      );
                    });
                  }}
                >
                  <ListViewColumn
                    id="Checkboxes"
                    title={
                      <Checkbox
                        checked={allVisibleDataChecked}
                        onChange={e =>
                          dispatch({
                            type:
                              ImplementedActionTypes.IMPLEMENTED_CHANGE_CHECKS,
                            ids: visibleData.map(i => i.external_id),
                            checked: e.target.checked,
                          })
                        }
                        color={colorVariables.midnight}
                      />
                    }
                    width={60}
                    template={(item: Space) => (
                      <div onMouseDown={e => e.stopPropagation()}>
                        <Checkbox
                          checked={state.selectedSpaceIds.includes(
                            item.external_id,
                          )}
                          onChange={e =>
                            dispatch({
                              type:
                                ImplementedActionTypes.IMPLEMENTED_CHANGE_CHECKS,
                              ids: [item.external_id],
                              checked: e.target.checked,
                            })
                          }
                          color={colorVariables.midnight}
                        />
                      </div>
                    )}
                  />
                  <ListViewColumn
                    id="name"
                    title="Name"
                    width={450}
                    template={(item: Space) => (
                      <SpaceNameAncestry
                        id={item.external_id}
                        name={item.name}
                        ancestry={item.ancestry.map(i => i.name)}
                      />
                    )}
                  />
                  <ListViewColumn
                    id="organization_name"
                    title="Organization"
                    width={400}
                    template={(item: Space) => item.organization_name}
                  />
                  <ListViewColumn
                    id="function"
                    title="Function"
                    width={150}
                    template={(item: Space) =>
                      item['function'] || <Fragment>&mdash;</Fragment>
                    }
                  />
                  <ListViewColumn
                    id="capacity"
                    title="Capacity"
                    width={100}
                    template={(item: Space) =>
                      item.capacity ? (
                        item.capacity
                      ) : (
                        <Fragment>&mdash;</Fragment>
                      )
                    }
                  />
                  <ListViewColumnSpacer />
                  <ListViewColumn
                    id="max_count_last_2_weeks"
                    title={
                      <ListViewTwoRowHeader
                        header="Peak Count"
                        subheader="Last 2 Weeks"
                      />
                    }
                    template={(item: Space) => item.max_count_last_2_weeks}
                    width={120}
                  />
                  <ListViewColumn
                    id="Details"
                    title=" "
                    width={100}
                    template={(item: Space) => (
                      <DetailsButton
                        href={`#/${item.organization_external_id}/spaces/${item.external_id}`}
                      />
                    )}
                  />
                </ListView>
              </div>
            </ReactInfiniteScroller>
          </div>
          {state.resource.data.fetchingMoreSpaces.status ===
            ResourceStatus.LOADING ? (
            <div className={styles.infiniteScrollLoading}>
              Loading more spaces...
            </div>
          ) : null}
          {state.resource.data.fetchingMoreSpaces.status ===
            ResourceStatus.ERROR ? (
            <div
              className={classnames(styles.infiniteScrollLoading, styles.error)}
            >
              Error loading spaces:{' '}
              {state.resource.data.fetchingMoreSpaces.error.toString()}
            </div>
          ) : null}
        </Fragment>
      );
  }
};

type SparklineProps = {
  width: number;
  height: number;
  points: Array<number>;
  pointColor: string;
};
const Sparkline: React.FunctionComponent<SparklineProps> = ({
  width,
  height,
  points,
  pointColor,
}) => {
  const distanceBetweenPoints = width / points.length;

  const padding = { top: 4, bottom: 4, left: 0, right: 4 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  let maxPoint = Math.max(0, ...points); // 2
  let minPoint = Math.min(0, ...points); // -2
  const maxPointHeight = innerHeight * 0.9;

  const distanceFromMinToMax = maxPoint !== minPoint ? maxPoint - minPoint : 1;

  const scaleX = index => (points.length - 1 - index) * distanceBetweenPoints;
  const scaleY = value =>
    innerHeight - ((value - minPoint) / distanceFromMinToMax) * maxPointHeight;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <g transform={`translate(${padding.left},${padding.top})`}>
        {/* Line at 0 */}
        <line
          x1={0}
          y1={scaleY(0)}
          x2={innerWidth}
          y2={scaleY(0)}
          stroke={colorVariables.gray700}
          strokeWidth={1}
          strokeLinecap="round"
          strokeDasharray={2}
        />

        {points.slice(0, -1).map((point, index) => (
          <line
            key={index}
            x1={scaleX(index)}
            y1={scaleY(points[index])}
            x2={scaleX(index + 1)}
            y2={scaleY(points[index + 1])}
            stroke={colorVariables.gray500}
            strokeWidth={2}
            strokeLinecap="round"
          />
        ))}
        <circle fill={pointColor} cx={scaleX(0)} cy={scaleY(points[0])} r={4} />
      </g>
    </svg>
  );
};

export default Implemented;
